import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { of, Subject, timer } from 'rxjs'
import { catchError, concatMap, delay, map, take, takeUntil, tap } from 'rxjs/operators'
import {
    AcceptConsumerTermsAndConditionsCommand,
    Role,
    TravelAgentBusinessOwnerInvitationStatus,
    UpdatePersonalPhoneNumberCommand
} from '../../../graphql'
import { Auth, RoleManager } from '../../injectable'
import { GqlQueries, InvitationService, RoleKeys } from '../../injectable/invitation-service'
import { UnsubscribeOnDestroy } from '../../utility'

@Component({
    selector: 'app-accept-invitation',
    templateUrl: 'accept-invitation.html'
})
export class AcceptInvitationComponent extends UnsubscribeOnDestroy implements OnInit {

    invitationId: string | null
    invitationRole: Role | undefined
    invitationEmail: string | null | undefined
    invitationFirstName: string | null | undefined
    invitationLastName: string | null | undefined
    invitationAccepted: string | null
    invitationPhoneNumber: string | null
    invitationAsset: string | null | undefined
    invitationStates = TravelAgentBusinessOwnerInvitationStatus
    showGenericErrorMsg = true
    roleKeys: RoleKeys
    gqlQueries: GqlQueries

    constructor(
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _invitationService: InvitationService,
        private _auth: Auth,
        private _roleManager: RoleManager,
    ) {

        super()

        this.invitationId = this._activatedRoute.snapshot.params.invitationId
        this.invitationRole = this._activatedRoute.snapshot.queryParams.role
        this.invitationAsset = this._activatedRoute.snapshot.queryParams.asset
        this.invitationPhoneNumber = this._activatedRoute.snapshot.queryParams.phone_number

        console.log('accept invitation ', this.invitationId)
        console.log('accept role ', this.invitationRole)
        console.log('accept phone number ', this.invitationPhoneNumber)
    }

    ngOnInit(): void {
        if (this._auth.isAuthenticated()) {
            if (this.invitationRole && this.invitationId && this.invitationAsset && this.invitationPhoneNumber) {
                this.roleKeys = this._invitationService.roleKeysByInvitationRole(
                    this.invitationRole,
                    this.invitationId,
                    this.invitationAsset
                )
                this.gqlQueries = this._invitationService.configureQueriesForRole(this.invitationRole)
                this.processInvitation()
            } else {
                // if logged in with potentially diff account - logout and redirect
                this._auth.logout()
                this.getInvitationDetailsAndRedirectToOnboarding()
            }
        }
         else {
            this.getInvitationDetailsAndRedirectToOnboarding()
        }
    }

    private getInvitationDetailsAndRedirectToOnboarding() {
        if (this.invitationId) {
            this._invitationService._invitationStatusGQL.fetch({
                invitationId: this.invitationId
            }).pipe(
                map(rs => rs.data.invitationStatus)
            ).subscribe((invitationDetails) => {
                this.invitationRole = invitationDetails?.role
                this.invitationEmail = invitationDetails?.email
                this.invitationFirstName = invitationDetails?.firstName
                this.invitationLastName = invitationDetails?.lastName
                this.invitationAccepted = invitationDetails?.acceptedOn
                this.invitationAsset = invitationDetails?.assetId

                if (this.invitationRole && this.invitationId) {
                    const queryParams = [
                        `invite_code=${this.invitationId}`,
                        `role=${this.invitationRole}`,
                        `email=${encodeURIComponent(this.invitationEmail)}`,
                        `firstName=${encodeURIComponent(this.invitationFirstName)}`,
                        `lastName=${encodeURIComponent(this.invitationLastName)}`,
                        `user_exists=${invitationDetails.userExistsOnIdentityProvider}`,
                        `invite_accepted=${this.invitationAccepted}`,
                        `asset=${this.invitationAsset}`
                    ].join('&')
                    of(this._router.navigateByUrl(`/onboarding?${queryParams}`)).subscribe()
                } else {
                    console.log('No role associated with invitation')
                }
            })
        }
    }

    private processInvitation() {
        of(true)
            .pipe(
                concatMap(() => this.checkInvitationIsValid$()),
                concatMap(() => this.acceptInvitation$()),
                concatMap(() => this.checkAccountCreation$()),
                concatMap(() => this.updatePhoneNumber$()),
                concatMap(() => this.acceptTerms$()),
                tap(() => {
                    this._roleManager.refresh()
                }),
                delay(5000),
                tap(() => {
                    of(this._router.navigateByUrl('/'))
                }),
                catchError(error => {
                    console.log('Error while processing invitation, redirecting - ', error)
                    const message = this.showGenericErrorMsg ? 'There was en error while processing your invitation. Please try again using the same email and password.' : error
                    this.handleError(message)
                    throw error
                })
            ).subscribe()
    }

    private handleError(message: string) {
        this._auth.logout()
        of(this._router.navigateByUrl(`/onboarding?error_message=${message}`)).subscribe()
    }

    private checkInvitationIsValid$() {
        return this.gqlQueries.validateInvitation
            .fetch({ invitationId: this.invitationId }, { fetchPolicy: 'no-cache' })
            .pipe(
                map((response: any) => {
                    const status = response?.data[this.roleKeys.invitation]?.status

                    if (response?.errors?.length) {
                        const message = `Error while checking invitation status: ${response.errors[0].message}`
                        throw new Error(message)
                    }
                    else if (!status || status !== this.invitationStates.Invited) {
                        this.showGenericErrorMsg = false
                        const message =
                            status === this.invitationStates.Cancelled ? 'This invitation has been cancelled. Please contact us for a new invite.' :
                            status === this.invitationStates.Accepted ? 'This invitation has already been accepted. Please login with your existing credentials.' :
                            'Invitation Invalid'
                        throw new Error(message)
                    }
                    return true
                })
            )
    }

    private acceptInvitation$() {
        const command = this.roleKeys.acceptCommand

        return this.gqlQueries.acceptInvitation
            .mutate(command)
            .pipe(
                map((response: any) => {
                    if (response?.errors?.length) {
                        const message = `Error accepting invitation: ${response.errors[0].message}`
                        throw new Error(message)
                    }
                    else if (!response?.data[this.roleKeys.accept]?.id) {
                        const message = 'No account id found'
                        throw new Error(message)
                    }
                    return response.data[this.roleKeys.accept].id
                })
            )
    }

    private checkAccountCreation$(){
        const subject$ = new Subject()

        timer(500)
            .pipe(
                take(30),
                takeUntil(subject$),
            )
            .subscribe((_) =>
                this._roleManager.queryAccountAsync(true)
                    .subscribe(account => {
                        if (account) {
                            console.log('Account created', account)
                            subject$.next(account)
                            subject$.complete()
                        } else {
                            console.log('Waiting for account creation')
                        }
                    }
                ))

        return subject$
    }

    private updatePhoneNumber$() {
        if (!this.invitationPhoneNumber) {
            throw new Error('Error: No invitation phone number')
        }

        const phoneNumber = JSON.parse(this.invitationPhoneNumber)

        const command: UpdatePersonalPhoneNumberCommand = {
            phoneNumber: {
                countryCode: phoneNumber.dialCode,
                nationalNumber: phoneNumber.nationalNumber
            }
        }

        return this._invitationService._updatePhoneGQL
            .mutate({ input: command })
            .pipe(
                map((response: any) => {
                    if (response?.errors?.length) {
                        const message = `Error updating phone number host invitation, ${response.errors[0].message}`
                        throw new Error(message)
                    }
                    return true
                })
            )
    }

    private acceptTerms$() {
        const command: AcceptConsumerTermsAndConditionsCommand = {
            accepted: true
        }

        return this._invitationService._acceptTermsGQL
            .mutate({ input: command })
            .pipe(
                map((response: any) => {
                    if (response?.errors?.length) {
                        const message = `Error accepting terms for invitation, ${response.errors[0].message}`
                        throw new Error(message)
                    }
                    return true
                })
            )
    }
}
